// Shared axios client, token management, interceptors
import { client, API_BASE, setToken, extractErrorMessage } from '@platform/http/api-client';

// Core Stride REST facade (search, issues, metadata, workflows, screens ...)
import coreApi from '@platform/http/api-core';

// Feature module API calls
import projectKickoffApi from '@modules/database/work-items/creators/frontend/api';
import foldersApi from '@modules/folders/frontend/api';
import recentlyViewedApi from '@modules/zebra-search/recently-viewed/frontend/api';
import notificationsApi from '@modules/notifications/frontend/api';
import zsdApi from '@client/dashboards/frontend/api';
import kanbanApi from '@modules/kanban/frontend/api';
import customsApi from '@modules/customs/frontend/api';
import gridsApi from '@modules/grids-gantts/frontend/api';
import filtersApi from '@modules/zebra-search/frontend/api';
import projectAttributesApi from '@modules/database/project-attributes/frontend/api';

// ==================== App-specific API calls ====================
// These stay here until their modules are extracted in later phases.

// Remaining app-specific API calls (navigation/sidebar/permissions — already served by app-shell module)
const appApi = {

  // Navigation
  async getNavigation() {
    const response = await client.get('/api/navigation');
    return response.data;
  },

  async saveNavigation(config) {
    const response = await client.put('/api/navigation', { config });
    return response.data;
  },

  // Sidebar
  async getSidebar() {
    const response = await client.get('/api/sidebar');
    return response.data;
  },

  async saveSidebar(config) {
    const response = await client.put('/api/sidebar', { config });
    return response.data;
  },

  // Workspaces — { main: { label, groups }, workspaces: [...] }. Switched off
  // for the client, the server answers an empty list and the one bar shows.
  async getWorkspaces() {
    const response = await client.get('/api/workspaces');
    return response.data;
  },

  async saveWorkspaces(definition) {
    const response = await client.put('/api/workspaces', definition);
    return response.data;
  },

  // Site switcher — { sites: [...] }, empty for anyone outside the
  // support group (the server decides; see app-shell/server/sites.js).
  async getSites() {
    const response = await client.get('/api/sites');
    return response.data;
  },

  async getBulkEditGroups() {
    const response = await client.get('/api/bulk-edit-groups');
    return response.data;
  },

  async saveBulkEditGroups(groups) {
    const response = await client.put('/api/bulk-edit-groups', { groups });
    return response.data;
  },

  async getBulkDeleteGroups() {
    const response = await client.get('/api/bulk-delete-groups');
    return response.data;
  },

  async saveBulkDeleteGroups(groups) {
    const response = await client.put('/api/bulk-delete-groups', { groups });
    return response.data;
  },

  // ── Supabase Auth (email + password) ───────────────────────────
  // Posts to the dual-mode auth backend; the server returns a JWT
  // signed with the same SESSION_SECRET the Atlassian-OAuth path
  // uses, so the rest of the app reads the token identically. Used
  // by the Home page login form for the post-disconnect login flow.
  async supabaseLogin(email, password) {
    const response = await client.post('/api/auth/supabase/login', { email, password });
    return response.data;
  },
  // Browser-side login flow: the frontend signs in against Supabase
  // Auth directly (see auth-supabase/frontend/supabaseClient.js) and
  // exchanges the Supabase access token for our app JWT here.
  async authExchange(accessToken) {
    const response = await client.post('/api/auth/exchange', { access_token: accessToken });
    return response.data;
  },
  async supabaseRequestPasswordReset(email) {
    const response = await client.post('/api/auth/supabase/request-password-reset', { email });
    return response.data;
  },
  async supabaseSetPassword(newPassword) {
    const response = await client.post('/api/auth/supabase/set-password', { newPassword });
    return response.data;
  },

  // ── Users & Groups admin (Settings → Users & Groups) ───────────
  // Admin-only user management on the Supabase-auth backend: mirror
  // users annotated with login state, invite/deactivate actions, and
  // group membership editing. All routes are requireAdmin-gated.
  async adminListUsers(search) {
    const response = await client.get('/api/admin/users', { params: search ? { search } : undefined });
    return response.data;
  },
  async adminProvisionAllUsers(dryRun) {
    const response = await client.post('/api/admin/users/provision-all', dryRun ? { dryRun: true } : {});
    return response.data;
  },
  // Bulk-set email_address on cloner-migrated users. Jira never sends
  // emails, so cloned rows land NULL and those users can't log in or be
  // invited. Run this BEFORE provisioning — emails lock once a login exists.
  async adminBulkSetUserEmails(entries, dryRun) {
    const response = await client.post('/api/admin/users/emails-bulk', { entries, ...(dryRun ? { dryRun: true } : {}) });
    return response.data;
  },
  async adminCreateUser(payload) {
    const response = await client.post('/api/admin/users', payload);
    return response.data;
  },
  async adminUpdateUser(accountId, updates) {
    const response = await client.patch(`/api/admin/users/${encodeURIComponent(accountId)}`, updates);
    return response.data;
  },
  async adminInviteUser(accountId) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/invite`);
    return response.data;
  },
  async adminDeactivateUser(accountId) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/deactivate`);
    return response.data;
  },
  async adminReactivateUser(accountId) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/reactivate`);
    return response.data;
  },
  async adminResetUserPassword(accountId) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/reset-password`);
    return response.data;
  },
  // Two-factor (TOTP) admin controls. requires_mfa is toggled through
  // adminUpdateUser({ requiresMfa }); these cover the factor-level
  // actions that need the Supabase service-role client server-side.
  async adminGetMfaStatus(accountId) {
    const response = await client.get(`/api/admin/users/${encodeURIComponent(accountId)}/mfa-status`);
    return response.data;
  },
  // Enrolment state for the whole users table in one call (the GET above
  // serves the detail view). Response: { [accountId]: { enrolled, factorCount } }.
  // Server caps at 200 ids per call — callers chunk (UsersGroupsAdmin does).
  async adminGetMfaStatusBulk(accountIds) {
    const response = await client.post('/api/admin/users/mfa-status-bulk', { accountIds });
    return response.data;
  },
  // MFA domain rules — "everyone @domain requires 2FA at sign-in".
  // Enforced by the login gate alongside the per-user and per-group flags.
  async adminListMfaDomainRules() {
    const response = await client.get('/api/admin/mfa-domain-rules');
    return response.data;
  },
  async adminCreateMfaDomainRule(emailDomain, requiresMfa = true) {
    const response = await client.post('/api/admin/mfa-domain-rules', { emailDomain, requiresMfa });
    return response.data;
  },
  async adminUpdateMfaDomainRule(emailDomain, requiresMfa) {
    const response = await client.put(`/api/admin/mfa-domain-rules/${encodeURIComponent(emailDomain)}`, { requiresMfa });
    return response.data;
  },
  async adminDeleteMfaDomainRule(emailDomain) {
    const response = await client.delete(`/api/admin/mfa-domain-rules/${encodeURIComponent(emailDomain)}`);
    return response.data;
  },
  async adminResetMfa(accountId) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/reset-2fa`);
    return response.data;
  },
  async adminDeleteUser(accountId) {
    const response = await client.delete(`/api/admin/users/${encodeURIComponent(accountId)}`);
    return response.data;
  },
  async adminUploadUserAvatar(accountId, dataUrl) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/avatar`, { dataUrl });
    return response.data;
  },
  async adminListGroups() {
    const response = await client.get('/api/admin/groups');
    return response.data;
  },
  // What membership of a group gives a person (see group-access-report.js).
  async adminGroupAccess(groupId) {
    const response = await client.get(`/api/admin/groups/${encodeURIComponent(groupId)}/access`);
    return response.data;
  },
  async adminCreateGroup(payload) {
    const response = await client.post('/api/admin/groups', payload);
    return response.data;
  },
  async adminUpdateGroup(groupId, updates) {
    const response = await client.patch(`/api/admin/groups/${encodeURIComponent(groupId)}`, updates);
    return response.data;
  },
  async adminDeleteGroup(groupId) {
    const response = await client.delete(`/api/admin/groups/${encodeURIComponent(groupId)}`);
    return response.data;
  },
  async adminAddGroupMember(groupId, accountId) {
    const response = await client.post(`/api/admin/groups/${encodeURIComponent(groupId)}/members`, { accountId });
    return response.data;
  },
  async adminRemoveGroupMember(groupId, accountId) {
    const response = await client.delete(`/api/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(accountId)}`);
    return response.data;
  },

  // ── Admin impersonation ("Log in as user") ─────────────────────
  // Mints a short-lived (60 min) app JWT carrying the TARGET's
  // identity plus an `act` claim naming the impersonating admin.
  // requireAdmin-gated server-side; nested impersonation is blocked.
  async adminImpersonate(accountId, reason) {
    const response = await client.post(`/api/admin/users/${encodeURIComponent(accountId)}/impersonate`, { reason });
    return response.data;
  },
  // Best-effort audit ping fired by the frontend when an admin leaves
  // an impersonation session. Swallows errors — the session already
  // ends locally regardless of whether the audit write lands.
  async impersonationEnd() {
    try {
      const response = await client.post('/api/auth/impersonation-end');
      return response.data;
    } catch {
      return null;
    }
  },

};

// Combine everything into one api object for backward compatibility
const api = {
  setToken,
  ...coreApi,
  ...projectKickoffApi,
  ...foldersApi,
  ...recentlyViewedApi,
  ...notificationsApi,
  ...zsdApi,
  ...kanbanApi,
  ...customsApi,
  ...gridsApi,
  ...filtersApi,
  ...projectAttributesApi,
  ...appApi,
};

export default api;
export { extractErrorMessage };
